import { useEffect, useMemo, useState, FC, Fragment, MouseEvent } from 'react'

import { useLocation } from 'react-router-dom'
import OutsideClickHandler from 'react-outside-click-handler'
import moment from 'moment'
import classNames from 'classnames'
import { useGetActiveDateRangeQuery } from 'src/reactQuery/useGetActiveDateRangeQuery'
import { useGetContentAnalysisActiveDateRangeQuery } from 'src/reactQuery/useGetContentAnalysisActiveDateRangeQuery'
import { useGetInboxActiveDateRangeQuery } from 'src/reactQuery/useGetInboxActiveDateRangeQuery'
import { useGetReviewAnalysisActiveDateRangeQuery } from 'src/reactQuery/useGetReviewAnalysisActiveDateRangeQuery'
import { useQueryParams } from 'src/hooks/useQueryParams'
import { GlobalQueryParams } from 'src/interfaces/globalQueryParams.interface'
import { LocalStorageKey } from 'src/localStorage/enums/localStorageKey.enum'
import { FilterPeriod } from 'src/constants/filterPeriods/enums/filterPeriod.enum'
import {
  getPeriodOptions,
  PERIOD_OPTIONS_TO_LABELS,
} from 'src/constants/filterPeriods'
import { getDateRangeByQueryParams } from 'src/utils/getDateRangeByQueryParams'
import { calendarContextFromPath } from 'src/utils/calendarContext'

import { HandleSelectRangeParams } from './interfaces/handleSelectRange.interface'
import { PopupType } from './enums/popupType.enum'
import styles from './topBarDatePicker.module.scss'
import { CustomCalendar } from './CustomCalendar'

const PROJECT_REGEX = /^\/projects\/[^/]+$/
const AI_LISTENING_REGEX = /^\/ai-listening(?:\/|$)/

// On the inbox-analysis listing page, Lifetime should always start
// from 2020-01-01 rather than the active range's firstDate.
const INBOX_LISTING_LIFETIME_START = '2020-01-01'

// All presets are always shown. The apply logic clamps the start to the
// campaign's first data date, so picking a window wider than the available
// data simply resolves to everything available.
const PERIOD_OPTION_KEYS: FilterPeriod[] = [
  FilterPeriod.TODAY,
  FilterPeriod.YESTERDAY,
  FilterPeriod.LAST_7_DAYS,
  FilterPeriod.LAST_30_DAYS,
  FilterPeriod.LAST_90_DAYS,
  FilterPeriod.CUSTOM,
  FilterPeriod.LIFETIME,
]

export const TopBarDatePicker: FC = () => {
  const { queryParams, updateQueryParams } = useQueryParams<GlobalQueryParams>()
  const location = useLocation()

  // Whose activity days the calendar dots show on this page — the same shared
  // builder the AI summary Time Filter uses, so both calendars agree.
  const calendarContext = useMemo(
    () => calendarContextFromPath(location.pathname),
    [location.pathname]
  )
  const { analysisId, source: calendarSource } = calendarContext
  // The inbox date range (Lifetime bounds) only applies to an inbox page id.
  const inboxPageId =
    calendarSource === 'inbox' ? calendarContext.pageId : undefined

  // The project dashboard is a multi-module overview: default to Lifetime
  // (when nothing is carried over) and treat Lifetime as true all-time rather
  // than clipping it to a single module's date window.
  const isProject = useMemo(
    () => PROJECT_REGEX.test(location.pathname),
    [location.pathname]
  )

  const [openPopupType, setOpenPopupType] = useState<PopupType | null>(null)

  const dates = getDateRangeByQueryParams(queryParams)

  const startDate = dates?.start
  const endDate = dates?.end

  const { data: activeDateRange } = useGetActiveDateRangeQuery()
  const { data: inboxDateRange } = useGetInboxActiveDateRangeQuery(inboxPageId)
  // Review analysis has its own history, independent of the account-wide range
  // below (which spans SOCIAL-LISTENING mentions). Where a campaign's reviews
  // predate its mentions, using the account range made "Lifetime" start after
  // most reviews and the dashboard read as empty.
  const reviewCampaignId =
    calendarSource === 'review-analysis' ? analysisId : undefined
  const { data: reviewDateRange } =
    useGetReviewAnalysisActiveDateRangeQuery(reviewCampaignId)
  // Content analysis likewise: an analysis's posts sit on their own timeline,
  // and the account-wide range moves whenever mentions are inserted. GMC
  // Motors (posts from June) showed 2 posts of 5 under Lifetime while the
  // account's mentions only went back to 9 August — then Lifetime silently
  // jumped to March the moment older mentions arrived. On the listing page
  // (no id) the bounds span every analysis the user owns.
  const isContentRoute = calendarSource === 'content'
  const { data: contentDateRange, isFetched: contentRangeFetched } =
    useGetContentAnalysisActiveDateRangeQuery({
      analysisId: isContentRoute ? analysisId : undefined,
      enabled: isContentRoute,
    })

  // Per-feature date range where the feature has one; account-wide otherwise.
  // A campaign with no reviews reports null bounds — fall through rather than
  // pin Lifetime to an empty window.
  //
  // Content routes report NO range until their bounds have been fetched: the
  // fill-in effect below would otherwise write the account-wide `from` first
  // and the re-anchor effect would then rewrite it — two navigations and a
  // flash of the wrong window. Once fetched, an analysis without posts (null
  // bounds) or a failed request falls through to the account range like
  // reviews do.
  const effectiveActiveDateRange =
    inboxPageId && inboxDateRange
      ? {
          firstDate: inboxDateRange.firstDate,
          lastDate: inboxDateRange.lastDate,
        }
      : reviewCampaignId &&
          reviewDateRange?.firstDate &&
          reviewDateRange?.lastDate
        ? {
            firstDate: reviewDateRange.firstDate,
            lastDate: reviewDateRange.lastDate,
          }
        : isContentRoute
          ? contentDateRange?.firstDate && contentDateRange?.lastDate
            ? {
                firstDate: contentDateRange.firstDate,
                lastDate: contentDateRange.lastDate,
              }
            : contentRangeFetched
              ? activeDateRange
              : undefined
          : activeDateRange

  const closeDropdown = () => {
    setOpenPopupType(null)
  }

  const handleSelectRange = (value: HandleSelectRangeParams): void => {
    const start = value.start?.startOf('day')?.toISOString(true)
    const end = value.end?.endOf('day')?.toISOString(true)

    if (start && end) {
      updateQueryParams({ from: start, period: FilterPeriod.CUSTOM, to: end })
      closeDropdown()
    }
  }

  const dropdown = (event: MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation()
    event.preventDefault()

    setOpenPopupType((prevState) =>
      prevState === null ? PopupType.SELECT : null
    )
  }

  const isInboxListing = calendarSource === 'inbox' && !inboxPageId

  const handleSelect = (period: FilterPeriod) => {
    if (
      effectiveActiveDateRange?.firstDate &&
      effectiveActiveDateRange?.lastDate
    ) {
      const activeFirst = moment.utc(effectiveActiveDateRange.firstDate)
      const activeLast = moment.utc(effectiveActiveDateRange.lastDate)
      const periodDates = getPeriodOptions()[period]

      const lifetimeStart = isInboxListing
        ? moment.utc(INBOX_LISTING_LIFETIME_START)
        : activeFirst

      const start = periodDates
        ? moment.max(periodDates.start, activeFirst)
        : lifetimeStart
      // Lifetime: extend through "now" instead of capping at the active
      // range's lastDate. The active range can lag behind raw data
      // (e.g. social-listening's lastDate on the inbox listing route),
      // which made lifetime end earlier than rolling windows like
      // "Last 90 days" and report fewer messages than they did.
      const end = periodDates
        ? periodDates.end
        : moment.max(activeLast, moment().endOf('day'))

      updateQueryParams({
        from: start.startOf('day').toISOString(true),
        period,
        to: end.endOf('day').toISOString(true),
      })
    } else {
      updateQueryParams({
        from: undefined,
        period,
        to: undefined,
      })
    }

    closeDropdown()
  }

  const handleClickCustom = () => {
    setOpenPopupType(PopupType.CALENDAR)
  }

  useEffect(() => {
    if (!queryParams.period) {
      const savedPeriod = localStorage.getItem(
        LocalStorageKey.DATE_FILTER_PERIOD
      ) as FilterPeriod | null

      // AI Listening prompts are checked weekly, so its screens open on a
      // month rather than a week.
      const fallback = isProject
        ? FilterPeriod.LIFETIME
        : AI_LISTENING_REGEX.test(location.pathname)
          ? FilterPeriod.LAST_30_DAYS
          : FilterPeriod.LAST_7_DAYS

      // A saved "custom" is meaningless to restore on its own: the from/to it
      // depends on live only in the URL (never persisted), so restoring it
      // without them collapses the picker to today-today. Fall back instead.
      const restored =
        savedPeriod && savedPeriod !== FilterPeriod.CUSTOM
          ? savedPeriod
          : fallback

      updateQueryParams({ period: restored })
    }
  }, [queryParams.period, isProject])

  useEffect(() => {
    if (queryParams.period) {
      localStorage.setItem(
        LocalStorageKey.DATE_FILTER_PERIOD,
        queryParams.period
      )
    }
  }, [queryParams.period])

  useEffect(() => {
    if (
      !isProject &&
      queryParams.period &&
      !queryParams.from &&
      effectiveActiveDateRange?.firstDate &&
      effectiveActiveDateRange?.lastDate
    ) {
      const activeFirst = moment.utc(effectiveActiveDateRange.firstDate)
      const activeLast = moment.utc(effectiveActiveDateRange.lastDate)
      const periodDates = getPeriodOptions()[queryParams.period]

      const lifetimeStart = isInboxListing
        ? moment.utc(INBOX_LISTING_LIFETIME_START)
        : activeFirst

      const start = periodDates
        ? moment.max(periodDates.start, activeFirst)
        : lifetimeStart
      // Same lifetime fix as in handleSelect — see comment there.
      const end = periodDates
        ? periodDates.end
        : moment.max(activeLast, moment().endOf('day'))

      updateQueryParams({
        from: start.startOf('day').toISOString(true),
        period: queryParams.period,
        to: end.endOf('day').toISOString(true),
      })
    }
  }, [
    queryParams.period,
    queryParams.from,
    effectiveActiveDateRange,
    isProject,
  ])

  // On the inbox-analysis listing page, enforce Lifetime's start as
  // 2020-01-01 even when navigating in with a stale `from` carried over
  // from another page or a previous session.
  useEffect(() => {
    if (
      !isInboxListing ||
      queryParams.period !== FilterPeriod.LIFETIME ||
      !queryParams.from
    ) {
      return
    }

    const expected = moment
      .utc(INBOX_LISTING_LIFETIME_START)
      .startOf('day')
      .toISOString(true)

    if (!moment(queryParams.from).isSame(moment(expected), 'day')) {
      updateQueryParams({ from: expected })
    }
  }, [isInboxListing, queryParams.period, queryParams.from])

  // Re-anchor a stale Lifetime `from` to the effective range's first date on
  // every other route. The fill-in effect above only runs when `from` is
  // ABSENT, so a `from` carried over in the URL — from another feature, from a
  // "Last 7 days" selection on a previous page, or a link shared earlier —
  // survives forever and silently filters the page down to a sliver while the
  // picker still reads "Lifetime". Campaigns showed "No Data" with
  // `from=2026-09-09` that way; BOG's reviews and GMC Motors' posts hit the
  // same bug before this was generalised from per-feature fixes.
  //
  // The project dashboard treats Lifetime as true all-time and the inbox
  // listing pins it to 2020-01-01 (handled above), so both are skipped here.
  useEffect(() => {
    if (
      isProject ||
      isInboxListing ||
      queryParams.period !== FilterPeriod.LIFETIME ||
      !queryParams.from ||
      !effectiveActiveDateRange?.firstDate
    ) {
      return
    }

    const expected = moment
      .utc(effectiveActiveDateRange.firstDate)
      .startOf('day')
      .toISOString(true)

    if (!moment(queryParams.from).isSame(moment(expected), 'day')) {
      updateQueryParams({ from: expected })
    }
    // updateQueryParams is intentionally omitted, matching the effects above.
  }, [
    isProject,
    isInboxListing,
    effectiveActiveDateRange?.firstDate,
    queryParams.period,
    queryParams.from,
  ])

  const calendarIsOpen = openPopupType === PopupType.CALENDAR

  return (
    <div className={styles.datepicker}>
      <OutsideClickHandler onOutsideClick={closeDropdown}>
        <div
          className={classNames(
            styles.dropdownEl,
            openPopupType === PopupType.SELECT && styles.expanded,
            queryParams.period === FilterPeriod.CUSTOM && styles.customDropdown
          )}
          onClick={dropdown}
        >
          <input
            defaultChecked
            id="check"
            name="sortType"
            type="radio"
            value={queryParams.period}
          />

          <label
            className={classNames(
              styles.labelCheck,
              queryParams.period === FilterPeriod.CUSTOM &&
                styles.flexDirectionColumn
            )}
            htmlFor="check"
          >
            <span
              className={classNames(
                styles.periodLabel,
                queryParams.period === FilterPeriod.CUSTOM &&
                  styles.customPeriod
              )}
            >
              {queryParams.period &&
                PERIOD_OPTIONS_TO_LABELS[queryParams.period]}
            </span>

            {queryParams.period === FilterPeriod.CUSTOM ? (
              <span className={styles.periodDates}>
                {moment(startDate).format('D MMMM YYYY')}
                &nbsp;-&nbsp;
                {moment(endDate).format('D MMMM YYYY')}
              </span>
            ) : null}
          </label>

          {PERIOD_OPTION_KEYS.map((key, index) => {
            return (
              <Fragment key={key}>
                <input
                  id={`id${index}`}
                  name="sortType"
                  type="radio"
                  value={key}
                />

                <label
                  htmlFor={`id${index}`}
                  onClick={(event) => {
                    event.stopPropagation()
                    event.preventDefault()

                    if (key === FilterPeriod.CUSTOM) {
                      handleClickCustom()
                      return
                    }

                    handleSelect(key)
                  }}
                >
                  {PERIOD_OPTIONS_TO_LABELS[key]}
                </label>
              </Fragment>
            )
          })}
        </div>

        {calendarIsOpen && (
          <CustomCalendar
            {...calendarContext}
            initialEnd={endDate ? moment(endDate) : null}
            initialStart={startDate ? moment(startDate) : null}
            onApply={(start, end) => {
              handleSelectRange({ end, start })
            }}
            onClose={closeDropdown}
          />
        )}
      </OutsideClickHandler>
    </div>
  )
}
